import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LoadingState {
  isLoading: boolean;
  message?: string;
}

/**
 * Serviço global de loading com suporte a stack de operações
 * Permite múltiplas chamadas simultâneas e exibição de mensagem opcional
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private readonly loadingSubject = new BehaviorSubject<LoadingState>({
    isLoading: false,
  });

  private loadingStack: Array<{ id: string; message?: string }> = [];
  private currentId = 0;

  /**
   * Observable para componentes observarem o estado de loading
   */
  public loading$: Observable<LoadingState> = this.loadingSubject.asObservable();

  /**
   * Inicia o loading com mensagem opcional
   * @param message Mensagem a ser exibida durante o loading
   * @returns ID único para controle desta operação de loading
   */
  show(message?: string): string {
    const id = `loading-${++this.currentId}`;
    this.loadingStack.push({ id, message });
    this.updateState();
    return id;
  }

  /**
   * Encerra o loading pelo ID
   * @param id ID retornado pelo método show()
   */
  hide(id?: string): void {
    if (id) {
      const index = this.loadingStack.findIndex((item) => item.id === id);
      if (index !== -1) {
        this.loadingStack.splice(index, 1);
      }
    } else {
      // Se não passar ID, remove o último
      this.loadingStack.pop();
    }
    this.updateState();
  }

  /**
   * Remove todas as operações de loading do stack
   * Atenção: Use apenas em casos especiais, preferencialmente use hide() com IDs específicos
   */
  hideAll(): void {
    this.loadingStack = [];
    this.updateState();
  }

  /**
   * Atualiza o estado do loading com base no stack
   */
  private updateState(): void {
    const isLoading = this.loadingStack.length > 0;
    const message = this.loadingStack.length > 0 ? this.loadingStack.at(-1)?.message : undefined;

    this.loadingSubject.next({
      isLoading,
      message,
    });
  }

  /**
   * Verifica se está carregando
   */
  isLoading(): boolean {
    return this.loadingStack.length > 0;
  }
}
